import { type FC } from "react";
import toast from "react-hot-toast";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button, Card, CardContent, Stack, TextField } from "@mui/material";
import { thunks } from "src/thunks/product";
import { useDispatch, useSelector } from "src/store";
import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";
import LoadingButton from "@mui/lab/LoadingButton";
import { Product } from "src/types/product";

// Define the Zod schema
const createValidationSchema = (t: any) =>
  z.object({
    name: z
      .string()
      .max(255, t(tokens.general.validators.maxLength255) as string)
      .min(1, t(tokens.general.validators.required) as string),
    description: z
      .string()
      .min(1, t(tokens.general.validators.required) as string),
    uniqueValueProposition: z
      .string()
      .max(255, t(tokens.general.validators.maxLength255) as string)
      .min(1, t(tokens.general.validators.required) as string),
    landingPage: z.string().optional(),
  });

// Define the Values type using Zod inference
type FormValues = z.infer<ReturnType<typeof createValidationSchema>>;

interface ProductEditFormProps {
  product: Product;
  onSave?: () => void;
  onCancel?: () => void;
}

export const ProductEditForm: FC<ProductEditFormProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { product, onSave, onCancel } = props;

  const { strategyId } = useSelector((state) => state.settings);

  // Create the validation schema
  const validationSchema = createValidationSchema(t);

  // Initialize the form
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      name: product.name,
      description: product.description,
      uniqueValueProposition: product.uniqueValueProposition,
      landingPage: product.landingPage,
    },
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = async (data: FormValues): Promise<void> => {
    try {
      const updatedProduct = await dispatch(
        thunks.updateProduct(strategyId!, product.id, {
          name: data.name,
          description: data.description,
          uniqueValueProposition: data.uniqueValueProposition,
          landingPage: data.landingPage,
        })
      );
      
      if (updatedProduct) {
        toast.success(t(tokens.products.details.form.successMessage));
        onSave?.();
      } else {
        toast.error(t(tokens.general.formError));
        setError("root", { message: "Update failed" });
      }
    } catch (err) {
      console.error(err);
      toast.error(t(tokens.general.formError));
      setError("root", { message: "Update failed" });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.name}
                    fullWidth
                    helperText={errors.name?.message}
                    label={t(tokens.products.details.form.name)}
                  />
                )}
              />

              <Controller
                name="uniqueValueProposition"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.uniqueValueProposition}
                    fullWidth
                    multiline
                    minRows={2}
                    helperText={errors.uniqueValueProposition?.message}
                    label={t(tokens.products.details.form.uniqueValueProposition)}
                  />
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.description}
                    fullWidth
                    multiline
                    minRows={5}
                    helperText={errors.description?.message}
                    label={t(tokens.products.details.form.description)}
                  />
                )}
              />
            </Stack>
          </CardContent>
        </Card>
        {/* 
        <Card sx={{ display: "flex", flexDirection: "column" }}>
          <CardHeader title={t(tokens.products.details.form.landingPage)} />
          <Divider />
          <CardContent>
            <Stack spacing={1}>
              <TextEditor
                ref={editorRef}
                customButtons={[
                  <TemplateButton
                    editor={editorRef.current}
                    onTextGenerate={(text) => {
                      editorRef?.current?.commands.setContent(text);
                    }}
                    getMessages={() => {
                      var messages = [
                        {
                          user: PromptMessageUser.System,
                          content: PROMPTS.TOPIC_CONTENT,
                        },
                      ] as PromptMessage[];

                      if (formik.values.name) {
                        messages.push({
                          user: PromptMessageUser.User,
                          content: `Product name:\n ${formik.values.name}`,
                        });
                      }

                      if (formik.values.description) {
                        messages.push({
                          user: PromptMessageUser.User,
                          content: `Product description:\n ${formik.values.description}`,
                        });
                      }

                      var targetAudiencesString = "";
                      formik.values.targetAudiences.map((x, index) => {
                        targetAudiencesString += `Target Audience ${index + 1}:\n`;
                        targetAudiencesString += `Name: ${x.name}\n\n`;
                        return true;
                      });

                      if (targetAudiencesString) {
                        messages.push({
                          user: PromptMessageUser.User,
                          content: `Target Audiences:\n ${targetAudiencesString}`,
                        });
                      }

                      return messages;
                    }}
                    onFinish={() => {
                      formik.setFieldValue(
                        "content",
                        editorRef?.current?.getHTML()
                      );
                    }}
                  />,
                ]}
                showHeaderTool={true}
                content={formik.values.landingPage ?? ""}
                onUpdate={(content) => {
                  formik.setFieldValue("landingPage", content.editor.getHTML());
                }}
              />
            </Stack>{" "}
          </CardContent>
        </Card> */}
        <Stack
          alignItems="center"
          direction="row-reverse"
          justifyContent="flex-end"
          spacing={1}
        >
          <Button
            onClick={onCancel}
            color="inherit"
          >
            {t(tokens.general.buttons.cancel)}
          </Button>
          <LoadingButton
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            variant="contained"
          >
            {t(tokens.general.buttons.update)}
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};
